import React, { useEffect, useReducer, useState, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import API from "./API";
import setAuthToken from "./setAuthToken";

let reducer = (auth, newAuth) => {
  if (newAuth === null) {
    localStorage.removeItem("auth");
    return initialAuth;
  }
  return { ...auth, ...newAuth };
};

const initialAuth = {
  token: null,
  isAuthenticated: false,
  loading: true,
  user: null,
};

const ADMIN_EMAIL = "adam@ellogovna.com";

const AuthContext = React.createContext({
  auth: initialAuth,
  setAuth: () => {},
  isAdmin: false,
  contributor: false,
  individualSubscription: false,
  workplaceSubscription: false,
  legislationAccess: false,
  teamsAccess: false,
  isAmbassador: false,
  referralToken: null,
  accessLoading: true,
  refreshStatus: () => {},
});

let validatedAuth = JSON.parse(localStorage.getItem("auth")) || initialAuth;

if (validatedAuth && validatedAuth.token) {
  try {
    const decoded = jwtDecode(validatedAuth.token);
    const currentTime = Date.now() / 1000;
    if (decoded.exp < currentTime) {
      localStorage.removeItem("auth");
      validatedAuth = initialAuth;
    } else {
      setAuthToken(validatedAuth.token);
    }
  } catch (err) {
    localStorage.removeItem("auth");
    validatedAuth = initialAuth;
  }
}

function AuthProvider(props) {
  const [auth, setAuth] = useReducer(reducer, validatedAuth);
  const [contributor, setContributor] = useState(false);
  const [individualSubscription, setIndividualSubscription] = useState(false);
  const [workplaceSubscription, setWorkplaceSubscription] = useState(false);
  const [workplaceSubscriber, setWorkplaceSubscriber] = useState(false);
  const [isAmbassador, setIsAmbassador] = useState(false);
  const [referralToken, setReferralToken] = useState(null);
  // Track which user ID we last completed a status fetch for.
  // accessLoading is derived: true whenever auth.user exists but status hasn't been fetched yet.
  // This ensures the gate components see accessLoading=true the instant auth.user is set,
  // preventing a one-render window where subscriptionsEnabled is loaded but individualSubscription
  // is still false, which caused an incorrect redirect to /subscribe on login.
  const [fetchedForUserId, setFetchedForUserId] = useState(null);
  const accessLoading = auth.user ? auth.user.user_id !== fetchedForUserId : false;

  const isAdmin = auth.user?.email === ADMIN_EMAIL;

  useEffect(() => {
    if (auth) {
      localStorage.setItem("auth", JSON.stringify(auth));
    }
  }, [auth]);

  const fetchStatus = useCallback((user_id) => {
    return API.getSubscriptionStatus(user_id)
      .then((res) => {
        setIndividualSubscription(res.data?.individual ?? false);
        setWorkplaceSubscription(res.data?.workplace ?? false);
        setWorkplaceSubscriber(res.data?.workplaceSubscriber ?? false);
        setContributor(res.data?.contributor ?? false);
        setIsAmbassador(res.data?.isAmbassador ?? false);
        setReferralToken(res.data?.referralToken ?? null);
      })
      .catch(() => {
        setIndividualSubscription(false);
        setWorkplaceSubscription(false);
        setWorkplaceSubscriber(false);
        setContributor(false);
        setIsAmbassador(false);
        setReferralToken(null);
      });
  }, []);

  useEffect(() => {
    if (auth.user) {
      fetchStatus(auth.user.user_id).finally(() => setFetchedForUserId(auth.user.user_id));
    } else {
      setIndividualSubscription(false);
      setWorkplaceSubscription(false);
      setWorkplaceSubscriber(false);
      setContributor(false);
      setIsAmbassador(false);
      setReferralToken(null);
      setFetchedForUserId(null);
    }
  }, [auth.user, fetchStatus]);

  const refreshStatus = useCallback(() => {
    if (auth.user) {
      fetchStatus(auth.user.user_id).finally(() => setFetchedForUserId(auth.user.user_id));
    }
  }, [auth.user, fetchStatus]);

  return (
    <AuthContext.Provider
      value={{
        auth,
        setAuth,
        isAdmin,
        contributor,
        individualSubscription,
        workplaceSubscription,
        workplaceSubscriber,
        legislationAccess: individualSubscription,
        teamsAccess: workplaceSubscription,
        isAmbassador,
        referralToken,
        accessLoading,
        refreshStatus,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
